import { useMemo, useState } from "react";
import { ApiError } from "@/api/client";
import {
  useApplyLeave,
  useCompOffBalance,
  useEditLeave,
  useHolidays,
  useLeaveTypes,
  useMyDocuments,
  useMyLeave,
  useMyLeaveBalance,
  useMyLeaveMonthlySummary,
  useWithdrawLeave,
  type LeaveInput,
} from "@/api/queries";
import { useSession } from "@/api/session";
import type { LeaveRequest, LeaveType } from "@/api/types";
import { PageHeader } from "@/app/page-header";
import { CategoryBarChart, StatTile, TrendChart } from "@/components/charts";
import { LeaveBalanceMeter } from "@/components/leave-balance-meter";
import { LeavePolicyPanel } from "@/components/leave-policy";
import { Badge, StatusBadge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
} from "@/components/ui/dialog";
import { Field, Input, Select, Textarea } from "@/components/ui/field";
import { Icon } from "@/components/ui/icon";
import { Panel, PanelHeader } from "@/components/ui/panel";
import { EmptyState, SkeletonTable } from "@/components/ui/states";
import { TBody, TD, TDPrimary, TH, THead, TR, TableWrap } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "@/components/ui/toast";
import { approvedDaysInYear, leaveDaysByMonth } from "@/lib/analytics";
import {
  countWorkingDays,
  currentMonth,
  earliestFilingDate,
  holidayMap,
  shiftMonth,
  todayStr,
} from "@/lib/calendar";
import { orgYear } from "@/lib/org-time";
import { formatDate, formatDays, formatTimestampDate } from "@/lib/format";
import { leaveLabel, UNRECORDED_ABSENCE_LABEL } from "@/lib/labels";
import { MyWfhPanel } from "@/pages/employee/wfh";
import { LeaveDetailDialog, ReasonCell } from "@/pages/shared/leave-detail-dialog";
import { WithdrawApprovedDialog } from "@/pages/shared/withdraw-approved-dialog";

export function MyLeavePage() {
  const myLeave = useMyLeave();
  const balance = useMyLeaveBalance();
  const types = useLeaveTypes();
  const compOff = useCompOffBalance();
  const withdraw = useWithdrawLeave();
  const documents = useMyDocuments();
  const isAdmin = useSession((s) => s.user?.role) === "admin";

  const myGender = documents.data?.profile.gender ?? null;
  // Straight off the account. This used to be inferred from whether an Earned
  // Leave balance had come back — which read "Intern" for everyone until that
  // query answered, so a full-timer was briefly told, in a table titled "Why you
  // get what you get", that they were not eligible for Earned Leave.
  const employmentType = useSession((s) => s.user?.employment_type);

  // Leave and work from home share this page but are NOT the same thing: one is
  // a day off that costs entitlement, the other a day worked that costs nothing.
  // They sit as sibling tabs rather than one merged list precisely so that
  // distinction survives — a combined table would need a "type" column doing all
  // the work, and would be read as a list of absences at a glance.
  const [tab, setTab] = useState<"leave" | "wfh">("leave");
  const [applying, setApplying] = useState(false);
  const [editing, setEditing] = useState<LeaveRequest | null>(null);
  const [viewing, setViewing] = useState<LeaveRequest | null>(null);
  // Handing back leave that was already granted is a different action from
  // deleting a request nobody has decided yet, so it gets its own confirmation
  // rather than sharing the pending path's one-click Withdraw.
  const [withdrawing, setWithdrawing] = useState<LeaveRequest | null>(null);
  const [summaryMonth, setSummaryMonth] = useState(currentMonth());
  const monthlySummary = useMyLeaveMonthlySummary(summaryMonth);

  const requests = myLeave.data ?? [];
  const pending = requests.filter((r) => r.status === "pending");
  // This year only — see approvedDaysInYear.
  const approvedDays = approvedDaysInYear(requests);

  const byMonth = useMemo(() => leaveDaysByMonth(requests), [requests]);
  const byType = useMemo(() => {
    const map = new Map<string, number>();
    for (const request of requests) {
      if (request.status !== "approved") continue;
      const label = leaveLabel(request.leave_type);
      map.set(label, (map.get(label) ?? 0) + request.days);
    }
    return [...map.entries()].map(([label, value]) => ({ label, value }));
  }, [requests]);

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        eyebrow="Your account"
        title="My leave"
        sub={
          tab === "leave"
            ? "Apply for leave, and see what you have taken and what is still available. You can edit or withdraw a request while it is still pending."
            : "Ask to work from home on particular days. A work-from-home day is a day worked, so it costs no leave."
        }
        actions={
          tab === "leave" ? (
            <Button variant="primary" iconLeft="plus" onClick={() => setApplying(true)}>
              Apply for leave
            </Button>
          ) : undefined
        }
      />

      <Tabs value={tab} onValueChange={(value) => setTab(value as "leave" | "wfh")}>
        <TabsList>
          <TabsTrigger value="leave">Leave</TabsTrigger>
          <TabsTrigger value="wfh">Work from home</TabsTrigger>
        </TabsList>

        <TabsContent value="leave" className="mt-6 flex flex-col gap-6">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatTile
          label="Requests pending"
          value={pending.length}
          icon="warning-circle"
          loading={myLeave.isLoading}
          meta={pending.length === 0 ? "Nothing outstanding" : "With your manager"}
        />
        <StatTile
          label="Days approved this year"
          value={approvedDays}
          icon="calendar-check"
          loading={myLeave.isLoading}
        />
        <StatTile
          label="Comp off available"
          value={formatDays(compOff.data?.available)}
          icon="check-circle"
          loading={compOff.isLoading}
          meta={`${formatDays(compOff.data?.earned)} earned, ${formatDays(compOff.data?.used)} used`}
        />
        <StatTile
          label="Leave types you can apply for"
          value={types.data?.length ?? 0}
          icon="leave"
          loading={types.isLoading}
          meta="Based on your employment type"
        />
      </div>

      {isAdmin && (
        /* An admin's request has no approver: /api/leave/:id/decide requires a
           manager on the requester's own team, and requires the requester to be
           role 'employee'. HR & Admin is neither. Saying so up front beats
           letting a request sit Pending forever with no explanation. */
        <Panel className="border-warn/40 bg-warn-wash/40">
          <div className="flex items-start gap-3 p-4">
            <Icon name="warning-circle" size={20} className="mt-0.5 shrink-0 text-warn" />
            <div className="min-w-0">
              <p className="text-sm font-medium text-ink">
                Your leave is recorded here, but nothing can approve it
              </p>
              <p className="mt-1 max-w-[70ch] text-sm leading-relaxed text-ink-soft">
                Approvals are made by a manager on the requester's own team. The HR and Admin
                account sits outside every team and is not an employee account, so no one in the
                system can action your requests: they will stay Pending. Use this to keep your own
                record of leave taken, and settle the actual approval outside the system until an
                approver is configured for this account.
              </p>
            </div>
          </div>
        </Panel>
      )}

      <Panel>
        <PanelHeader
          title="Your balance"
          note={`Calendar year ${balance.data?.year ?? orgYear()} · scoped to what you are eligible for`}
        />
        <div className="p-4">
          {balance.isLoading ? (
            <SkeletonTable rows={4} cols={2} />
          ) : Object.keys(balance.data?.balance ?? {}).length === 0 ? (
            <EmptyState icon="leave" title="No entitlements on file yet" />
          ) : (
            <div className="grid gap-x-8 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
              {Object.entries(balance.data?.balance ?? {}).map(([type, line]) =>
                line ? (
                  <div key={type} className="flex flex-col gap-1">
                    <LeaveBalanceMeter type={type} line={line} />
                    {line.entitlement === null && (
                      <p className="text-xs leading-relaxed text-muted">
                        No annual cap is defined for this type yet, so only what you have taken is
                        tracked.
                      </p>
                    )}
                    {type === "CompOff" && (
                      <p className="text-xs leading-relaxed text-muted">
                        Earned by working a weekly off or holiday — the total is what the ledger has
                        credited you, not an annual entitlement.
                      </p>
                    )}
                  </div>
                ) : null,
              )}
            </div>
          )}
        </div>
      </Panel>

      <Panel>
        <PanelHeader
          title="Leave summary"
          note={formatDate(`${summaryMonth}-01`, "MMMM yyyy") ?? summaryMonth}
          actions={
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                aria-label="Previous month"
                onClick={() => setSummaryMonth(shiftMonth(summaryMonth, -1))}
              >
                <Icon name="caret-left" size={16} />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Next month"
                disabled={summaryMonth >= currentMonth()}
                onClick={() => setSummaryMonth(shiftMonth(summaryMonth, 1))}
              >
                <Icon name="caret-right" size={16} />
              </Button>
            </div>
          }
        />
        <div className="p-4">
          {monthlySummary.isLoading ? (
            <SkeletonTable rows={4} cols={2} />
          ) : (
            <TableWrap>
              <THead>
                <TH>Type</TH>
                <TH numeric>Days taken</TH>
              </THead>
              <TBody>
                {Object.entries(monthlySummary.data?.byType ?? {}).map(([type, days]) => (
                  <TR key={type}>
                    <TDPrimary>{leaveLabel(type)}</TDPrimary>
                    <TD numeric>{formatDays(days)}</TD>
                  </TR>
                ))}
                <TR>
                  <TDPrimary>{UNRECORDED_ABSENCE_LABEL}</TDPrimary>
                  <TD numeric>{formatDays(monthlySummary.data?.lwp ?? 0)}</TD>
                </TR>
              </TBody>
            </TableWrap>
          )}
        </div>
      </Panel>

      <div className="grid gap-4 xl:grid-cols-2">
        <TrendChart
          title="Your approved leave by month"
          note={`Calendar year ${balance.data?.year ?? orgYear()}`}
          data={byMonth}
          xKey="month"
          series={[{ key: "days", label: "Days approved" }]}
          area
          loading={myLeave.isLoading}
          emptyBody="You have no approved leave this year yet."
        />

        <CategoryBarChart
          title="Days taken by type"
          note="Approved requests only"
          data={byType}
          valueHeader="Days"
          loading={myLeave.isLoading}
          emptyBody="You have not taken any leave yet."
        />
      </div>

      <Panel>
        <PanelHeader title="Your requests" note="Newest first" />
        {myLeave.isLoading ? (
          <SkeletonTable rows={5} cols={5} />
        ) : requests.length === 0 ? (
          <EmptyState
            icon="leave"
            title="You have not applied for leave yet"
            body="Apply for leave and it goes straight to your manager for a decision."
            action={
              <Button variant="primary" iconLeft="plus" onClick={() => setApplying(true)}>
                Apply for leave
              </Button>
            }
          />
        ) : (
          <TableWrap>
            <THead>
              <TH>Type</TH>
              <TH>Dates</TH>
              <TH numeric>Days</TH>
              <TH>Reason</TH>
              <TH>Status</TH>
              <TH>
                <span className="sr-only">Actions</span>
              </TH>
            </THead>
            <TBody>
              {requests.map((request) => (
                <TR key={request.id} onClick={() => setViewing(request)}>
                  <TDPrimary>{leaveLabel(request.leave_type)}</TDPrimary>
                  <TD className="whitespace-nowrap font-mono text-xs">
                    {formatDate(request.start_date, "d MMM")}
                    {request.start_date !== request.end_date &&
                      ` to ${formatDate(request.end_date, "d MMM")}`}
                  </TD>
                  <TD numeric>
                    {request.days}
                    {request.approved_days !== undefined &&
                      request.approved_days > request.days && (
                        <span
                          className="ml-1 text-xs font-normal text-muted"
                          title={`${request.approved_days - request.days} day(s) withdrawn from this request`}
                        >
                          of {request.approved_days}
                        </span>
                      )}
                  </TD>
                  <TD className="max-w-[26ch]">
                    <ReasonCell reason={request.reason} onOpen={() => setViewing(request)} />
                  </TD>
                  <TD>
                    <StatusBadge status={request.status} />
                  </TD>
                  <TD>
                    {request.status === "pending" ? (
                      <span className="flex items-center gap-1">
                        {/* The row opens the detail dialog, so controls inside
                            it stop the click propagating. */}
                        <Button
                          variant="ghost"
                          size="sm"
                          iconLeft="edit"
                          onClick={(event) => {
                            event.stopPropagation();
                            setEditing(request);
                          }}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          iconLeft="trash"
                          onClick={(event) => {
                            event.stopPropagation();
                            withdraw.mutate(request.id, {
                              onSuccess: () => toast.success("Request withdrawn."),
                              onError: (err) =>
                                toast.error(
                                  err instanceof ApiError ? err.message : "Could not withdraw that.",
                                ),
                            });
                          }}
                        >
                          Withdraw
                        </Button>
                      </span>
                    ) : request.status === "approved" ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        iconLeft="arrow-up"
                        onClick={(event) => {
                          event.stopPropagation();
                          setWithdrawing(request);
                        }}
                      >
                        Withdraw
                      </Button>
                    ) : (
                      <span className="whitespace-nowrap text-xs text-faint">
                        {request.decided_at
                          ? `Decided ${formatTimestampDate(request.decided_at, "d MMM")}`
                          : "—"}
                      </span>
                    )}
                  </TD>
                </TR>
              ))}
            </TBody>
          </TableWrap>
        )}
      </Panel>

      <LeavePolicyPanel
        title="Why you get what you get"
        note="Your employment type and gender decide which of these apply to you."
        scoped
        employmentType={employmentType}
        gender={myGender}
      />
        </TabsContent>

        <TabsContent value="wfh" className="mt-6">
          <MyWfhPanel />
        </TabsContent>
      </Tabs>

      {viewing && <LeaveDetailDialog request={viewing} onClose={() => setViewing(null)} />}

      {withdrawing && (
        <WithdrawApprovedDialog request={withdrawing} onClose={() => setWithdrawing(null)} />
      )}

      {(applying || editing) && (
        <LeaveDialog
          request={editing}
          types={types.data ?? []}
          onClose={() => {
            setApplying(false);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

function LeaveDialog({
  request,
  types,
  onClose,
}: {
  request: LeaveRequest | null;
  types: LeaveType[];
  onClose: () => void;
}) {
  const apply = useApplyLeave();
  const edit = useEditLeave();
  const holidays = useHolidays();

  /*
    Empty until the eligible list arrives, rather than falling back to "CL".
    That fallback fired whenever this dialog mounted before /api/leave/types
    answered: the Select rendered no options at all while the state quietly held
    Casual Leave, so a blank field submitted CL. Editing an existing request
    still starts from what that request actually is.
  */
  const [leaveType, setLeaveType] = useState<LeaveType | "">(request?.leave_type ?? "");
  const [startDate, setStartDate] = useState(request?.start_date ?? todayStr());
  const [endDate, setEndDate] = useState(request?.end_date ?? todayStr());
  const [reason, setReason] = useState(request?.reason ?? "");
  const [error, setError] = useState<string | null>(null);

  const isOptionalHoliday = leaveType === "OptionalHoliday";
  // Leave already taken can be filed afterwards, back to the 1st of this month;
  // it reaches the approver marked as filed late. A pending request being edited
  // keeps its own date even once the month has turned.
  const earliest = earliestFilingDate();
  const minStart = request && request.start_date < earliest ? request.start_date : earliest;
  const optionalHolidays = (holidays.data ?? []).filter(
    (h) => h.category === "optional" && h.date >= minStart,
  );
  // Working days only — weekends and fixed holidays inside the range aren't
  // charged, matching what the backend will actually record.
  const fixedHolidaySet = useMemo(() => new Set(holidayMap(holidays.data).keys()), [holidays.data]);
  const busy = apply.isPending || edit.isPending;

  async function submit() {
    setError(null);
    if (!leaveType) {
      setError("Pick which type of leave this is.");
      return;
    }
    if (!startDate || !endDate) {
      setError("Both dates are required.");
      return;
    }
    if (startDate > endDate) {
      setError("The start date cannot be after the end date.");
      return;
    }
    if (startDate < earliest && startDate !== request?.start_date) {
      setError(`Past days can only be filed within the current month — pick ${formatDate(earliest)} or later.`);
      return;
    }
    if (!isOptionalHoliday && !reason.trim()) {
      setError("Give a reason for this request.");
      return;
    }

    const payload: LeaveInput = { startDate, endDate, reason: reason.trim(), leaveType };
    try {
      if (request) {
        await edit.mutateAsync({ ...payload, id: request.id });
        toast.success("Request updated.");
      } else {
        await apply.mutateAsync(payload);
        toast.success("Leave request submitted.");
      }
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not submit that request.");
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader
          title={request ? "Edit your request" : "Apply for leave"}
          description={
            request
              ? "A request can only be changed while it is still pending."
              : "This goes to your manager for a decision. Forgot to apply? You can file for days earlier this month too."
          }
        />
        <DialogBody className="flex flex-col gap-4">
          {error && (
            <p
              role="alert"
              className="flex items-start gap-2 rounded-lg bg-bad-wash px-3 py-2.5 text-[13px] leading-relaxed text-bad"
            >
              <Icon name="warning-circle" size={16} className="mt-0.5" />
              <span>{error}</span>
            </p>
          )}

          <Field label="Leave type" htmlFor="lv-type" required>
            <Select
              id="lv-type"
              value={leaveType}
              disabled={types.length === 0}
              onChange={(e) => setLeaveType(e.target.value as LeaveType)}
            >
              <option value="">
                {types.length === 0 ? "Loading your leave types…" : "Select a leave type…"}
              </option>
              {types.map((type) => (
                <option key={type} value={type}>
                  {leaveLabel(type)}
                </option>
              ))}
            </Select>
          </Field>

          {isOptionalHoliday ? (
            <Field
              label="Which optional holiday"
              htmlFor="lv-optional"
              required
              hint="Must be one of the floating holidays on the company calendar. You can take up to two a year."
            >
              <Select
                id="lv-optional"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setEndDate(e.target.value);
                }}
              >
                <option value="">Select a holiday</option>
                {optionalHolidays.map((holiday) => (
                  <option key={holiday.date} value={holiday.date}>
                    {formatDate(holiday.date, "d MMM")} · {holiday.name}
                  </option>
                ))}
              </Select>
            </Field>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="From" htmlFor="lv-start" required>
                <Input
                  id="lv-start"
                  type="date"
                  min={minStart}
                  value={startDate}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    if (e.target.value > endDate) setEndDate(e.target.value);
                  }}
                />
              </Field>
              <Field label="To" htmlFor="lv-end" required>
                <Input
                  id="lv-end"
                  type="date"
                  min={startDate}
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </Field>
            </div>
          )}

          {!isOptionalHoliday && (
            <Field label="Reason" htmlFor="lv-reason" required>
              <Textarea
                id="lv-reason"
                placeholder="Family function"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </Field>
          )}

          {startDate && endDate && startDate <= endDate && (
            <p className="flex items-center gap-1.5 text-[13px] text-muted">
              <Icon name="calendar" size={16} className="text-faint" />
              <Badge tone="outline">
                {countWorkingDays(startDate, endDate, fixedHolidaySet)}{" "}
                {countWorkingDays(startDate, endDate, fixedHolidaySet) === 1 ? "day" : "days"}
              </Badge>
              <span className="text-faint">Weekends and holidays in this range aren&rsquo;t counted</span>
            </p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit} loading={busy}>
            {request ? "Save changes" : "Submit request"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
